/*****************************************************
 * ZendingTijdlijn.jsx
 * Locatie: src/components/CustomerPortal/ZendingTijdlijn.jsx
 * Visuele tijdlijn van de zendingstatus — zoals DHL/FedEx.
 *****************************************************/

import React from 'react';
import {
  FileText, Tag, ThumbsUp, CheckCircle, Truck, Star
} from 'lucide-react';

// Stappen in volgorde
const STAPPEN = [
  {
    key: 'Aanvraag ingediend',
    label: 'Aangevraagd',
    icon: FileText,
    interneStatussen: ['Prijzen nog aan te vragen', 'Prijzen uitgezet', 'Prijzen vervoerders binnen'],
  },
  {
    key: 'Prijs ontvangen',
    label: 'Prijs ontvangen',
    icon: Tag,
    interneStatussen: ['Prijs geoffreerd'],
  },
  {
    key: 'Akkoord gegeven',
    label: 'Akkoord gegeven',
    icon: ThumbsUp,
    interneStatussen: ['Opdracht ontvangen'],
  },
  {
    key: 'Opdracht bevestigd',
    label: 'Bevestigd',
    icon: CheckCircle,
    interneStatussen: ['Opdracht verzonden', 'Opdracht bevestigd'],
  },
  {
    key: 'Onderweg',
    label: 'Onderweg',
    icon: Truck,
    interneStatussen: ['Onderweg'],
  },
  {
    key: 'Afgerond',
    label: 'Afgeleverd',
    icon: Star,
    interneStatussen: ['Afgeleverd', 'Afgerond'],
  },
];

// Bepaal huidige stapindex op basis van interne status en laaddatum.
// Als de status "Opdracht bevestigd/verzonden" is maar de laaddatum
// is vandaag of in het verleden, toon dan automatisch "Onderweg" (stap 4).
function bepaalStapIndex(interneStatus, laadDatum) {
  if (!interneStatus) return 0;
  const s = interneStatus.toLowerCase();

  if (s.includes('afgeleverd') || s.includes('afgerond')) return 5;
  if (s.includes('onderweg')) return 4;

  if (s.includes('opdracht verzonden') || s.includes('opdracht bevestigd')) {
    if (laadDatum) {
      const laden = new Date(laadDatum);
      laden.setHours(0, 0, 0, 0);
      const vandaag = new Date();
      vandaag.setHours(0, 0, 0, 0);
      if (laden <= vandaag) return 4; // laaddatum verstreken → Onderweg
    }
    return 3;
  }

  if (s.includes('opdracht ontvangen')) return 2;
  if (s.includes('prijs geoffreerd')) return 1;
  if (s.includes('afgeschreven')) return -1;
  return 0;
}

const NAVY  = '#1a364c';
const GOLD  = '#d4a848';
const GRAY  = '#9ca3af';
const GREEN = '#16a34a';

export default function ZendingTijdlijn({ aanvraag }) {
  const interneStatus = aanvraag?.Status || '';
  const laadDatum     = aanvraag?.LaadDatum || null;
  const huidigeStap   = bepaalStapIndex(interneStatus, laadDatum);
  const geannuleerd   = huidigeStap === -1;

  return (
    <div style={{ padding: '20px 16px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>

      {/* Titel rij */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: NAVY, textTransform: 'uppercase', letterSpacing: '.06em' }}>
          Zendingstatus
        </span>
        {geannuleerd ? (
          <span style={{ fontSize: 12, fontWeight: 600, background: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: 20 }}>
            Geannuleerd
          </span>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 600, background: '#dbeafe', color: '#1e40af', padding: '3px 10px', borderRadius: 20 }}>
            {STAPPEN[Math.max(0, huidigeStap)]?.label}
          </span>
        )}
      </div>

      {/* Tijdlijn */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, position: 'relative' }}>
        {STAPPEN.map((stap, i) => {
          const Icoon      = stap.icon;
          const gedaan     = !geannuleerd && i <= huidigeStap;
          const huidig     = !geannuleerd && i === huidigeStap;
          const isLaatste  = i === STAPPEN.length - 1;

          const cirkelBg  = geannuleerd ? '#f1f5f9'
                          : gedaan && i === STAPPEN.length - 1 ? GREEN
                          : gedaan ? NAVY
                          : '#f1f5f9';
          const cirkelRand = geannuleerd ? GRAY
                           : gedaan ? (i === STAPPEN.length - 1 ? GREEN : NAVY)
                           : GRAY;
          const icoKleur  = gedaan ? '#fff' : geannuleerd ? GRAY : '#cbd5e1';
          const labelKleur = gedaan && !geannuleerd ? NAVY : GRAY;
          const lijnKleur  = gedaan && i < huidigeStap ? NAVY : '#e2e8f0';

          return (
            <React.Fragment key={stap.key}>
              {/* Stap */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: isLaatste ? '0 0 auto' : undefined }}>
                {/* Cirkel */}
                <div style={{
                  width: huidig ? 40 : 32,
                  height: huidig ? 40 : 32,
                  borderRadius: '50%',
                  background: cirkelBg,
                  border: `2px solid ${cirkelRand}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all .2s',
                  boxShadow: huidig ? `0 0 0 4px ${NAVY}22` : 'none',
                  position: 'relative',
                  zIndex: 2,
                }}>
                  <Icoon size={huidig ? 18 : 14} color={icoKleur} strokeWidth={2} />
                  {/* Gouden ring voor huidige stap */}
                  {huidig && (
                    <div style={{
                      position: 'absolute', inset: -5,
                      borderRadius: '50%',
                      border: `2px solid ${GOLD}`,
                      pointerEvents: 'none',
                    }} />
                  )}
                </div>
                {/* Label */}
                <div style={{
                  marginTop: 8,
                  fontSize: 10,
                  fontWeight: huidig ? 700 : 500,
                  color: labelKleur,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  letterSpacing: '.01em',
                }}>
                  {stap.label}
                </div>
              </div>

              {/* Verbindingslijn */}
              {!isLaatste && (
                <div style={{
                  flex: 1,
                  height: 2,
                  background: lijnKleur,
                  marginBottom: 22,
                  transition: 'background .3s',
                  minWidth: 8,
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Subtekst — contextgevoelig per stap */}
      {!geannuleerd && (
        <div style={{ marginTop: 12, fontSize: 11, color: '#6b7280', textAlign: 'center' }}>
          {huidigeStap === 0 && 'Uw aanvraag is ontvangen. We werken aan een prijsopgave.'}
          {huidigeStap === 1 && 'U heeft een prijsvoorstel ontvangen. Geef akkoord om de opdracht te bevestigen.'}
          {huidigeStap === 2 && 'Akkoord ontvangen. Wij bevestigen de opdracht bij de vervoerder.'}
          {huidigeStap === 3 && 'De opdracht is bevestigd. De vervoerder is ingepland voor ophaling.'}
          {huidigeStap === 4 && 'Uw zending is onderweg. Volg de locatie via Track & Trace.'}
          {huidigeStap === 5 && 'Uw zending is afgeleverd. Dank voor uw vertrouwen in SendWell.'}
        </div>
      )}
      {geannuleerd && (
        <div style={{ marginTop: 12, fontSize: 11, color: '#ef4444', textAlign: 'center' }}>
          Deze zending is geannuleerd. Neem contact op als u vragen heeft.
        </div>
      )}
    </div>
  );
}
